/**
 * The toolbar that never goes away.
 *
 * Modes on the left, the tools that act on a selection in the middle, and what
 * the world itself needs on the right. The arrangement is borrowed from the
 * engine editors on purpose: somebody who has used one should not have to learn
 * where anything is, and the same tools should be in the same place whatever is
 * being edited.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { EditSummary } from './edits';
import { MODES, type EditorMode, type Selection } from './model';
import type { WindowName } from './windows';

/**
 * How tall the bar turned out to be, published for everything under it.
 *
 * It wraps when there is not room for one row, and a second row that lands on
 * top of the world, the hover banner and the ping is worse than a bar that takes
 * the room it needs. Measured rather than assumed, so the answer stays right
 * through a resize or a button appearing.
 */
function useMeasuredHeight(): (element: HTMLDivElement | null) => void {
  return useCallback((element: HTMLDivElement | null) => {
    if (!element) {
      document.documentElement.style.removeProperty('--editor-bar-height');
      return;
    }

    const publish = (): void => {
      const height = Math.round(element.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--editor-bar-height', `${height}px`);
    };

    publish();

    const watcher = new ResizeObserver(publish);
    watcher.observe(element);
  }, []);
}

export function EditorToolbar({
  mode,
  onMode,
  selection,
  hint,
  onFind,
  saving,
  world,
  panned,
  onFocus,
  onFollow,
  grid,
  onGrid,
  blocked,
  onBlocked,
  scenery,
  onScenery,
  onStandHere,
  canTeleport,
  storey,
  roofOff,
  onRoofOff,
  onStorey,
  docking,
  dockLift,
  hang,
  onHang,
  onDocking,
  onNewDock,
  panels,
  onPanel,
  onClose,
  onUndo,
  onRedo,
  onUndoTo,
  onRedoTo,
  onEditMode,
  onPublish,
  onDiscard,
  onSave,
  onSaveEverything,
}: {
  mode: EditorMode;
  onMode: (mode: EditorMode) => void;
  selection: Selection;
  hint: string;
  /** Opens the search across everything the world is made of. */
  onFind: () => void;
  saving: boolean;
  /** What the server says about the world: unsaved work, and what can be taken back. */
  world: {
    unsaved: boolean;
    canUndo: boolean;
    canRedo: boolean;
    undo: string;
    redo: string;
    /** Whether work is being held back from everybody else, and how much of it. */
    drafting: boolean;
    draftBy: string;
    draftChanges: number;
    draftSquares: number;
    /** What could be taken back, newest first, and what could be put back again. */
    history: readonly EditSummary[];
    future: readonly EditSummary[];
  };
  /** Whether changes are seen as they happen, or held until they are published. */
  onEditMode: (mode: 'live' | 'batch') => void;
  onPublish: () => void;
  onDiscard: () => void;
  /** Whether the view has been taken off the character. */
  panned: boolean;
  onFocus: () => void;
  onFollow: () => void;
  /** Whether the tile grid is drawn, which is how things get lined up. */
  grid: boolean;
  onGrid: (grid: boolean) => void;
  /** Whether closed tiles are outlined in red. */
  blocked: boolean;
  onBlocked: (blocked: boolean) => void;
  /** Whether what is standing on the ground is drawn at all. */
  scenery: boolean;
  onScenery: (scenery: boolean) => void;
  /** Sends the character to the tile that is held, for trying out what was built. */
  onStandHere: () => void;
  canTeleport: boolean;
  /** Which storey is being built on, and how to change it. */
  storey: number;
  onStorey: (storey: number) => void;
  /** Whether everything above that storey is hidden while it is worked on. */
  roofOff: boolean;
  onRoofOff: (off: boolean) => void;
  /** Whether the dock tool is on, and the height the dock is being built to. */
  docking: boolean;
  dockLift: number | null;
  /** How far above the ground the next thing is put down, in tiles. */
  hang: number;
  onHang: (hang: number) => void;
  onDocking: (docking: boolean) => void;
  /** Forgets that height, so the next piece put down starts a new dock. */
  onNewDock: () => void;
  /** Which windows are open, and how to open or put away one of them. */
  panels: ReadonlySet<WindowName>;
  onPanel: (name: WindowName) => void;
  /** Puts the editor down and gives the world back to the character. */
  onClose: () => void;
  onUndo: () => void;
  onRedo: () => void;
  /** Back, or forward, that many changes at once. */
  onUndoTo: (steps: number) => void;
  onRedoTo: (steps: number) => void;
  onSave: () => void;
  onSaveEverything: () => void;
}): JSX.Element {
  // What the tools would act on, rather than everything a click put up for
  // choosing between.
  const holding = selection.gathered ? selection.picks.length : selection.picks.length > 0 ? 1 : 0;

  const measure = useMeasuredHeight();

  return (
    <div className="editor-bar" ref={measure}>
      <span className="editor-brand">Editor</span>

      <div className="editor-group modes">
        {MODES.map((entry) => (
          <button
            key={entry.mode}
            type="button"
            className={mode === entry.mode ? 'editor-mode on' : 'editor-mode'}
            title={`${entry.hint} (${entry.key})`}
            onClick={() => onMode(entry.mode)}
          >
            {entry.label}
          </button>
        ))}

        {/* What the mode being used is for, beside the mode rather than five
            groups further along the bar, where it read as a stray label. Cut
            short when the bar is full, with the whole of it on the tooltip: a
            line of guidance that ends in an ellipsis has stopped guiding. */}
        <span className="editor-hint" title={hint}>
          {hint}
        </span>
      </div>

      <span className="editor-divider" />

      <div className="editor-group view">
        <button
          type="button"
          className="editor-tool"
          disabled={holding === 0}
          title="Look at what is selected (F)"
          onClick={onFocus}
        >
          Focus
        </button>
        <button
          type="button"
          className={panned ? 'editor-tool on' : 'editor-tool'}
          disabled={!panned}
          title="Put the view back on your character (G)"
          onClick={onFollow}
        >
          Follow
        </button>
        <button
          type="button"
          className={grid ? 'editor-tool on' : 'editor-tool'}
          title="Draw the tile grid"
          onClick={() => onGrid(!grid)}
        >
          Grid
        </button>
        {/* What is closed to walking is invisible from the ground, which is how
            a tile stays blocked long after whatever blocked it came down. */}
        <button
          type="button"
          className={blocked ? 'editor-tool on' : 'editor-tool'}
          title="Outline every tile nothing can stand on"
          onClick={() => onBlocked(!blocked)}
        >
          Blocked
        </button>
        <button
          type="button"
          className="editor-tool"
          disabled={!canTeleport}
          title={
            canTeleport
              ? 'Put your character on the tile that is held, to try what is there'
              : 'Hold a tile to be sent to it'
          }
          onClick={onStandHere}
        >
          Teleport
        </button>
        <button
          type="button"
          className={scenery ? 'editor-tool' : 'editor-tool on'}
          title="Take everything standing on the ground out of the way"
          onClick={() => onScenery(!scenery)}
        >
          {scenery ? 'Hide' : 'Hidden'}
        </button>
      </div>

      <span className="editor-divider" />

      <div className="editor-group history">
        <button
          type="button"
          className="editor-tool"
          disabled={!world.canUndo}
          title={world.canUndo ? `Take back: ${world.undo} (Ctrl Z)` : 'Nothing to take back'}
          onClick={onUndo}
        >
          Undo
        </button>
        <button
          type="button"
          className="editor-tool"
          disabled={!world.canRedo}
          title={world.canRedo ? `Put back: ${world.redo} (Ctrl Y)` : 'Nothing to put back'}
          onClick={onRedo}
        >
          Redo
        </button>
        <HistoryList
          history={world.history}
          future={world.future}
          onUndoTo={onUndoTo}
          onRedoTo={onRedoTo}
        />
      </div>

      <span className="editor-divider" />

      {/* Whether the world is being changed in front of everybody, or in a batch
          only this person sees. A batch holds every square it touches as it
          stood, so publishing is the moment everybody else's ground catches up,
          and nobody has to reload anything for it. */}
      <div className="editor-group batching">
        <select
          className="editor-pick"
          title={
            world.drafting
              ? 'Batch editing. Nobody else sees any of this until you publish it.'
              : 'Live editing. Everything you do is seen by everybody as you do it.'
          }
          value={world.drafting ? 'batch' : 'live'}
          onChange={(event) => onEditMode(event.target.value === 'batch' ? 'batch' : 'live')}
        >
          <option value="live">Live Edit</option>
          <option value="batch">Batch Edit</option>
        </select>
        {world.drafting ? (
          <>
            <button
              type="button"
              className="editor-tool go"
              disabled={world.draftChanges === 0}
              title={
                world.draftChanges === 0
                  ? 'Nothing has been changed yet'
                  : `Show everybody ${world.draftChanges} ${world.draftChanges === 1 ? 'change' : 'changes'} over ${world.draftSquares} ${world.draftSquares === 1 ? 'square' : 'squares'}, and write it out`
              }
              onClick={onPublish}
            >
              Publish
            </button>
            <button
              type="button"
              className="editor-tool"
              disabled={world.draftChanges === 0}
              title={
                world.draftChanges === 0
                  ? 'Nothing has been changed yet'
                  : `Throw away ${world.draftChanges} ${world.draftChanges === 1 ? 'change' : 'changes'} and put the world back`
              }
              onClick={onDiscard}
            >
              Discard
            </button>
            <span className="editor-count" title="Changes waiting to be published">
              {world.draftChanges}
            </span>
          </>
        ) : null}
      </div>

      {/* What height things are built at: a storey, or out over water to one
          level. Four buttons that were only ever one answer between them, so
          one box that says which answer it is. */}
      <span className="editor-divider" />

      <div className="editor-group storeys">
        <select
          className="editor-pick"
          title={
            docking
              ? 'Building to one height. The first piece you put down sets it, and every piece after it is laid level with it however deep the water gets.'
              : 'Which storey things are built on. Lay floors upstairs first: it is solid until something is built to stand on, and stairs below are how anybody reaches it.'
          }
          value={docking ? 'dock' : String(storey)}
          onChange={(event) => {
            const picked = event.target.value;

            // A dock is built at the water rather than upstairs, so choosing it
            // puts the work back on the ground.
            if (picked === 'dock') {
              onStorey(0);
              onDocking(true);
              return;
            }

            onDocking(false);
            onStorey(Number(picked));
          }}
        >
          <option value="0">Ground</option>
          <option value="1">Up 1</option>
          <option value="2">Up 2</option>
          <option value="dock">Dock</option>
        </select>

        {/* Building an upper floor with the roof still on is building blind.
            Only offered upstairs, since on the ground it would hide nothing
            and mean nothing. */}
        {storey > 0 && !docking ? (
          <button
            type="button"
            className={roofOff ? 'editor-tool on' : 'editor-tool'}
            title={
              roofOff
                ? 'Showing this storey and everything under it. Press to put the storeys above back.'
                : 'Hide everything above this storey, so an upper floor can be worked on from above.'
            }
            onClick={() => onRoofOff(!roofOff)}
          >
            {roofOff ? 'Roof off' : 'Roof on'}
          </button>
        ) : null}

        {/* How high off the ground the next thing goes. A string of lights is
            the reason this exists: it follows the road and hangs over it,
            which is a different thing from a dock laid level. */}
        {!docking ? (
          <label className="editor-number" title="How far above the ground the next thing is put down">
            <span>hang</span>
            <input
              type="number"
              step={0.5}
              min={0}
              max={8}
              value={hang}
              onChange={(event) => onHang(Math.min(8, Math.max(0, Number(event.target.value) || 0)))}
            />
          </label>
        ) : null}

        {docking ? (
          <button
            type="button"
            className="editor-tool"
            disabled={dockLift === null}
            title="Forget that height. The next piece you put down starts a new dock and sets its own."
            onClick={onNewDock}
          >
            {dockLift === null ? 'No height yet' : `At ${dockLift.toFixed(2)}`}
          </button>
        ) : null}
      </div>

      {holding > 0 ? <span className="editor-count">{holding} selected</span> : null}

      {/* Which panels are on screen. Everything the editor can show is here, so
          a window somebody put away has one obvious place to come back from. */}
      <span className="editor-divider" />

      <div className="editor-group panels">
        {(
          [
            ['world', 'World'],
            ['map', 'Map'],
            ['make', 'Create'],
            ['quests', 'Quests'],
            ['talk', 'Dialogue'],
            ['keys', 'Keys'],
          ] as const
        ).map(([name, label]) => (
          <button
            key={name}
            type="button"
            className={panels.has(name) ? 'editor-tool on' : 'editor-tool'}
            title={panels.has(name) ? `Put the ${label} panel away` : `Show the ${label} panel`}
            onClick={() => onPanel(name)}
          >
            {label}
          </button>
        ))}
      </div>

      <span className="editor-divider" />

      {/* Everything that ends something, together at the far end of the bar:
          look for a thing, write the work out, put the editor down. They were
          scattered along the row, which meant the two buttons that write to disk
          sat between two that do not. */}
      <div className="editor-group leave">
        {/* One way in to everything, rather than remembering which tab a thing
            lives behind. Ctrl F does the same. */}
        <button
          type="button"
          className="editor-tool editor-find"
          title="Find anything: items, characters, scenery, quests, models (Ctrl F)"
          aria-label="Find"
          onClick={onFind}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <circle cx="6.8" cy="6.8" r="4.4" />
            <line x1="10.1" y1="10.1" x2="14" y2="14" />
          </svg>
        </button>

        {/* Saving and publishing are the same press while a batch is open, so
            only one of them is offered: Save is held shut and says why, rather
            than being pressed and refused. */}
        <button
          type="button"
          className={world.unsaved && !world.drafting ? 'editor-save waiting' : 'editor-save'}
          onClick={onSave}
          disabled={saving || (world.drafting && world.draftChanges > 0)}
          title={
            world.drafting && world.draftChanges > 0
              ? 'There is a batch open. Publish it, and it is written out with it.'
              : world.unsaved
                ? 'Writes the ground, the scenery and the characters standing in this place out to disk. Nothing else.'
                : 'Nothing about this place has changed since the last save'
          }
        >
          {/* Work that has not been written out is said on the button that writes
              it out, rather than as a word of its own further along the bar. It
              was the widest thing on there that nobody could press. */}
          {saving ? 'Saving' : 'Save'}
        </button>

        <button
          type="button"
          className="editor-save"
          onClick={onSaveEverything}
          disabled={saving || (world.drafting && world.draftChanges > 0)}
          title={
            world.drafting && world.draftChanges > 0
              ? 'There is a batch open. Publish it first.'
              : 'The same, and every content file with it: items, characters, quests, conversations, shops and drop tables. Use this after writing anything that is not the ground itself.'
          }
        >
          Save all
        </button>

        <button
          type="button"
          className="editor-tool editor-close"
          title="Put the editor down and play"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
}

function ago(at: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

/**
 * Everything the two buttons would walk through, with any point in it one
 * press away. Pressing a change takes back it and everything after it.
 */
function HistoryList({
  history,
  future,
  onUndoTo,
  onRedoTo,
}: {
  history: readonly EditSummary[];
  future: readonly EditSummary[];
  onUndoTo: (steps: number) => void;
  onRedoTo: (steps: number) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const box = useRef<HTMLDivElement>(null);

  // A press anywhere else puts it away, the way a menu goes.
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent): void => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener('pointerdown', away, true);
    return () => window.removeEventListener('pointerdown', away, true);
  }, [open]);
  const line = (one: EditSummary): string =>
    `${one.by}, ${ago(one.at, now)}${one.size > 1 ? `, ${one.size} changes` : ''}`;

  return (
    <div className="editor-history" ref={box}>
      <button
        type="button"
        className={open ? 'editor-tool on' : 'editor-tool'}
        title="Everything that can be taken back or put back"
        disabled={history.length === 0 && future.length === 0}
        onClick={() => {
          setNow(Date.now());
          setOpen(!open);
        }}
      >
        ▾
      </button>
      {open ? (
        <div className="editor-history-list">
          {future.length > 0 ? (
            <>
              <h5>Taken back</h5>
              {[...future].reverse().map((one, at, all) => (
                <button
                  key={`f${at}`}
                  type="button"
                  className="editor-history-row undone"
                  title="Put back everything up to and including this"
                  onClick={() => {
                    onRedoTo(all.length - at);
                    setOpen(false);
                  }}
                >
                  <span>{one.what}</span>
                  <small>{line(one)}</small>
                </button>
              ))}
            </>
          ) : null}
          <h5>Done</h5>
          {history.length === 0 ? <p className="hint">Nothing yet.</p> : null}
          {history.map((one, at) => (
            <button
              key={`d${at}`}
              type="button"
              className="editor-history-row"
              title="Take back this and everything after it"
              onClick={() => {
                onUndoTo(at + 1);
                setOpen(false);
              }}
            >
              <span>{one.what}</span>
              <small>{line(one)}</small>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
